import { LayerData, tLayerSettings, userViewerSettingsAtom } from '@/atoms';
import { colours } from '@/core';
import { PositionGroup2 } from '@/core/iprj';
import { useLayerSettingsMany } from '@/hooks/useLayerSettings';
import { useFrame, useLoader, useThree } from '@react-three/fiber';
import { useAtomValue } from 'jotai';
import { Suspense, useEffect, useMemo, useRef, useState, type Dispatch, type SetStateAction } from 'react';
import * as THREE from 'three';
import { Loader } from './Loader';
import { MinimalHotspot } from './MinimalHotspot';

import logoURL from '@/assets/logo.png';

const imageFilterVertexShader = /* glsl */ `
varying vec2 vUv;
void main() {
  vUv = uv;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`;

// Equirectangular textures need U flipped (repeat.x = -1, offset.x = 1)
// which built-in materials handle automatically but ShaderMaterial does not.
const equirectVertexShader = /* glsl */ `
varying vec2 vUv;
void main() {
  vUv = vec2(1.0 - uv.x, uv.y);
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`;

const imageFilterFragmentShader = /* glsl */ `
uniform sampler2D map;
uniform float brightness;
uniform float contrast;
uniform float saturation;
varying vec2 vUv;

// Linear → sRGB (matches Three.js LinearTosRGB)
vec3 linearToSrgb(vec3 c) {
  vec3 lo = c * 12.92;
  vec3 hi = 1.055 * pow(c, vec3(1.0 / 2.4)) - 0.055;
  return mix(lo, hi, step(0.0031308, c));
}

void main() {
  vec4 texColor = texture2D(map, vUv);
  vec3 color = texColor.rgb;

  // Brightness (additive)
  color += brightness;

  // Contrast (scale around midpoint 0.5)
  color = (color - 0.5) * contrast + 0.5;

  // Saturation (mix with luminance)
  float lum = dot(color, vec3(0.2126, 0.7152, 0.0722));
  color = mix(vec3(lum), color, saturation);

  gl_FragColor = vec4(linearToSrgb(clamp(color, 0.0, 1.0)), texColor.a);
}
`;

function createImageFilterMaterial(
  texture: THREE.Texture,
  side: THREE.Side,
  settings: tLayerSettings,
): THREE.ShaderMaterial {
  return new THREE.ShaderMaterial({
    uniforms: {
      map: { value: texture },
      brightness: { value: (settings.imageBrightness ?? 0) / 100 },
      contrast: { value: 1 + (settings.imageContrast ?? 0) / 100 },
      saturation: { value: 1 + (settings.imageSaturation ?? 0) / 100 },
    },
    vertexShader: imageFilterVertexShader,
    fragmentShader: imageFilterFragmentShader,
    side,
    depthWrite: false,
    depthTest: false,
    toneMapped: false,
    fog: false,
  });
}

/**
 * Equirectangular panorama entry.
 *
 * Uses a stable ShaderMaterial held in a ref so that filter-slider updates
 * only poke uniforms instead of swapping materials / toggling children,
 * which was causing R3F to re-reconcile the mesh and rotate the sphere.
 */
function EquirectEntry({
  entry,
  texture,
  sphereGeom,
  settings,
}: {
  entry: PositionGroup2['entries'][number];
  texture: THREE.Texture;
  sphereGeom: THREE.SphereGeometry;
  settings: tLayerSettings | undefined;
}) {
  const matRef = useRef<THREE.ShaderMaterial | null>(null);

  // Create the material once
  if (!matRef.current) {
    matRef.current = new THREE.ShaderMaterial({
      uniforms: {
        map: { value: texture },
        brightness: { value: 0 },
        contrast: { value: 1 },
        saturation: { value: 1 },
      },
      vertexShader: equirectVertexShader,
      fragmentShader: imageFilterFragmentShader,
      side: THREE.BackSide,
      depthWrite: false,
      depthTest: false,
      toneMapped: false,
      fog: false,
    });
  }

  // Update uniforms when texture or settings change — no new material, no reconciliation
  useEffect(() => {
    const mat = matRef.current!;
    mat.uniforms.map.value = texture;
    mat.uniforms.brightness.value = (settings?.imageBrightness ?? 0) / 100;
    mat.uniforms.contrast.value = 1 + (settings?.imageContrast ?? 0) / 100;
    mat.uniforms.saturation.value = 1 + (settings?.imageSaturation ?? 0) / 100;
  }, [texture, settings?.imageBrightness, settings?.imageContrast, settings?.imageSaturation]);

  const q = useMemo(
    () =>
      new THREE.Quaternion(
        entry.rotationQuaternion[0],
        entry.rotationQuaternion[1],
        entry.rotationQuaternion[2],
        entry.rotationQuaternion[3],
      ),
    [
      entry.rotationQuaternion[0],
      entry.rotationQuaternion[1],
      entry.rotationQuaternion[2],
      entry.rotationQuaternion[3],
    ],
  );

  return (
    <mesh quaternion={q} renderOrder={-9999} material={matRef.current}>
      <primitive object={sphereGeom} />
    </mesh>
  );
}

/**
 * Creates a repeated-logo fallback texture for equirectangular use.
 * Best used on a sphere or other large surface where you want the logo
 * repeated all around.
 */
export async function createLogoGridTexture(
  logoUrl: string,
  options?: {
    size?: number; // canvas size
    tileSize?: number; // size of each repeated tile
    logoScale?: number; // how large the logo is inside each tile
    background?: string; // optional background fill
    opacity?: number; // logo opacity
  },
): Promise<THREE.Texture> {
  const {
    size = 1024,
    tileSize = 256,
    logoScale = 0.3,
    background = 'rgba(255,255,255,0.0)',
    opacity = 1,
  } = options || {};

  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;

  const ctx = canvas.getContext('2d')!;
  ctx.clearRect(0, 0, size, size);

  if (background) {
    ctx.fillStyle = background;
    ctx.fillRect(0, 0, size, size);
  }

  const img = await loadImage(logoUrl);

  const drawSize = tileSize * logoScale;

  for (let y = 0; y < size; y += tileSize) {
    for (let x = 0; x < size; x += tileSize) {
      const dx = x + (tileSize - drawSize) / 2;
      const dy = y + (tileSize - drawSize) / 2;

      ctx.save();
      ctx.globalAlpha = opacity;

      // Optional: alternate slight rotation for a nicer pattern
      const cx = x + tileSize / 2;
      const cy = y + tileSize / 2;
      ctx.translate(cx, cy);
      ctx.rotate((((x / tileSize + y / tileSize) % 2 === 0 ? 0 : 15) * Math.PI) / 180);
      ctx.drawImage(img, -drawSize / 2, -drawSize / 2, drawSize, drawSize);
      ctx.restore();
    }
  }

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.needsUpdate = true;

  return texture;
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

/**
 * Custom texture loader that uses fetch with credentials.
 * On failure, resolves with an opaque alpha-grid texture instead of throwing.
 */
class CredentialsTextureLoader extends THREE.Loader<THREE.Texture> {
  load(
    url: string,
    onLoad?: (texture: THREE.Texture) => void,
    _onProgress?: (event: ProgressEvent) => void,
    _onError?: (err: unknown) => void,
  ): THREE.Texture {
    const texture = new THREE.Texture();

    fetch(url, { credentials: 'include' })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.blob();
      })
      .then((blob) => createImageBitmap(blob, { imageOrientation: 'flipY' }))
      .then((bitmap) => {
        texture.image = bitmap;
        texture.needsUpdate = true;
        onLoad?.(texture);
      })
      .catch((err) => {
        console.warn(`[IPRJ2] Failed to load texture: ${url}`, err);
        createLogoGridTexture(logoURL).then((fallback) => {
          texture.image = fallback.image;
          texture.needsUpdate = true;
          onLoad?.(texture);
        });
      });

    return texture;
  }
}

export const IPRJ = ({
  setPosition,
  // images,
  imagePositions: positions,
  layers,
  imageId,
  setImageId,
  hoverImageId,
  setHoverImageId,
  position,
}: {
  setPosition: Dispatch<SetStateAction<THREE.Vector3>>;
  visible?: boolean;
  images: Omit<Image, 'layer'>[] | null;
  imagePositions:
    | (PositionGroup2 & { cameraPosition: THREE.Vector3; renderPosition: THREE.Vector3; display: boolean })[]
    | null;
  layers: LayerData[] | null;
  position: THREE.Vector3;
  imageId: null | string;
  setImageId: Dispatch<SetStateAction<string | null>>;
  hoverImageId: string | null;
  setHoverImageId: Dispatch<SetStateAction<string | null>>;
}) => {
  const canvas = useThree((state) => state.gl.domElement);
  const layerSettings = useLayerSettingsMany(layers?.map((l) => l.id) || []);

  const viewerSettings = useAtomValue(userViewerSettingsAtom);
  const renderImagePOIs = viewerSettings?.showImagePOIs ?? true;

  const imagePosition = positions?.find((p) => p.entries[0].id === imageId);

  useEffect(() => {
    if (!imageId || !imagePosition) return;
    if (
      imagePosition.cameraPosition.x === position.x &&
      imagePosition.cameraPosition.y === position.y &&
      imagePosition.cameraPosition.z === position.z
    )
      return;

    setPosition(imagePosition.cameraPosition.clone());
  }, [imageId, positions, setPosition]);

  // Prefetch textures on hover so clicking feels instant
  useEffect(() => {
    if (!hoverImageId || !positions || !layers) return;
    if (hoverImageId === imageId) return;

    const hoveredPosition = positions.find((p) => p.entries[0].id === hoverImageId);
    if (!hoveredPosition) return;

    const layer = layers.find((l) => l.id === hoveredPosition.entries[0].layerId);
    console.log('layer', layer);
    if (!layer) return;

    // Low-res URLs (same logic as IPRJSkybox's initialUrls)
    const lowResUrls = hoveredPosition.entries.map((image) => {
      const filename =
        image.hasCompressedVariants && image.smallFilename
          ? image.smallFilename
          : image.largeFilename || image.filename;
      return `${import.meta.env.VITE_RESOURCES_ENDPOINT}/${layer.root}/${filename}`;
    });

    // Warm r3f's useLoader cache for low-res textures
    useLoader.preload(CredentialsTextureLoader, lowResUrls);

    // Warm HTTP cache for high-res textures
    hoveredPosition.entries.forEach((image) => {
      if (image.hasCompressedVariants && image.smallFilename) {
        const filename = image.largeFilename || image.filename;
        const url = `${import.meta.env.VITE_RESOURCES_ENDPOINT}/${layer.root}/${filename}`;
        fetch(url, { credentials: 'include' }).catch(() => {});
      }
    });
  }, [hoverImageId, imageId, positions, layers]);

  if (!positions || positions.length === 0 || !imageId) return null;

  return (
    <>
      <Suspense fallback={<Loader />}>
        {imagePosition ? (
          <IPRJSkybox
            position={imagePosition}
            layers={layers}
            setPosition={setPosition}
            layerSettings={layerSettings}
          />
        ) : null}
      </Suspense>
      <group>
        {/* optional: global postprocessing bloom */}
        {/* <EffectComposer multisampling={4}>
          <Bloom
            intensity={0.7}
            luminanceThreshold={0.2}
            luminanceSmoothing={0.9}
            blendFunction={BlendFunction.SCREEN}
          />
        </EffectComposer> */}

        {renderImagePOIs
          ? positions
              .filter((p) => p.display)
              .map((p, i) => (
                <MinimalHotspot
                  key={i}
                  position={p.renderPosition}
                  active={imageId === p.entries[0].id}
                  hovered={hoverImageId === p.entries[0].id}
                  colorDefault={
                    layerSettings?.[p.entries[0].layerId as string]?.imagePointColor || colours.images.default
                  }
                  colorHover={colours.images.hover}
                  colorActive={colours.images.active}
                  onPointerEnter={() => {
                    if (imageId === p.entries[0].id) return;
                    setHoverImageId(p.entries[0].id);
                    canvas.style.cursor = 'pointer';
                  }}
                  onPointerLeave={() => {
                    setHoverImageId(null);
                    canvas.style.cursor = 'auto';
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setImageId(p.entries[0].id);
                  }}
                />
              ))
          : null}
      </group>
    </>
  );
};

function applyEquirectangularTextureSettings(texture: THREE.Texture, maxAnisotropy: number): void {
  texture.wrapS = THREE.RepeatWrapping;
  texture.repeat.x = -1;
  texture.offset.x = 1;
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.generateMipmaps = true;
  texture.minFilter = THREE.LinearMipmapLinearFilter;
  texture.anisotropy = Math.min(8, maxAnisotropy);
}

function getDirectionVector(headingDeg: number, rollDeg: number, pitchDeg: number): number[] {
  // Convert to radians (negate heading and roll for X,Y)
  const h = (-headingDeg * Math.PI) / 180;
  const r = (-rollDeg * Math.PI) / 180;
  const p = (pitchDeg * Math.PI) / 180;

  const ch = Math.cos(h),
    sh = Math.sin(h);
  const cp = Math.cos(p),
    sp = Math.sin(p);
  const cr = Math.cos(r),
    sr = Math.sin(r);

  // X and Y from HRP rotation of [0,0,1]
  const x = ch * sp + sh * sr * cp;
  const y = sh * sp - ch * sr * cp;

  // Z uses original (unnegated) roll
  const r_original = (rollDeg * Math.PI) / 180;
  const z = Math.sin(p) * Math.cos(r_original);

  return [x, y, z];
}

function quatFromHRP(hrp: [number, number, number], rotationOrder: 1 | 2 | 3 | 4 | 6): THREE.Quaternion {
  const [hDeg, rDeg, pDeg] = hrp;
  const h = THREE.MathUtils.degToRad(hDeg);
  const r = THREE.MathUtils.degToRad(rDeg);
  const p = THREE.MathUtils.degToRad(pDeg);

  // per spec: Heading = rot around Z, Roll = rot around X, Pitch = rot around Y
  const H = new THREE.Matrix4().makeRotationZ(h);
  const R = new THREE.Matrix4().makeRotationX(r);
  const P = new THREE.Matrix4().makeRotationY(p);

  // multiply in the exact order from the .iprj file (left→right)
  // 1 – Heading * Pitch * Roll
  // 2 – Heading * Roll * Pitch
  // 3 – Roll * Pitch * Heading
  // 4 – Pitch * Roll * Heading
  const m = new THREE.Matrix4().identity();
  switch (rotationOrder) {
    case 1:
      m.multiply(H).multiply(P).multiply(R);
      break;
    case 2:
      m.multiply(H).multiply(R).multiply(P);
      break;
    case 3:
      m.multiply(R).multiply(P).multiply(H);
      break;
    case 4:
      m.multiply(P).multiply(R).multiply(H);
      break;
    default:
      throw new Error('Invalid rotation order: ' + rotationOrder);
  }

  return new THREE.Quaternion().setFromRotationMatrix(m);
}

const IPRJSkybox = ({
  position,
  layers,
  layerSettings,
}: {
  position: PositionGroup2;
  layers: LayerData[] | null;
  setPosition: Dispatch<SetStateAction<THREE.Vector3>>;
  layerSettings: Record<string, tLayerSettings>;
}) => {
  const camera = useThree((state) => state.camera);
  const gl = useThree((state) => state.gl);

  const layer = useMemo(() => {
    if (!layers) return null;
    return layers.find((l) => l.id === position.entries[0].layerId) || null;
  }, [position, layers]);

  useFrame(() => {
    if (groupRef.current) {
      groupRef.current.position.copy(camera.position);
    }
  });

  // Determine which images to load initially (small if available, otherwise large/original)
  const initialUrls = useMemo(() => {
    if (!layer) return [];
    return position.entries.map((image) => {
      const filename =
        image.hasCompressedVariants && image.smallFilename
          ? image.smallFilename
          : image.largeFilename || image.filename;
      return `${import.meta.env.VITE_RESOURCES_ENDPOINT}/${layer.root}/${filename}`;
    });
  }, [position.entries, layer]);

  // URLs for large images (only if we have compressed variants)
  const largeUrls = useMemo(() => {
    if (!layer) return [];
    return position.entries.map((image) => {
      if (image.hasCompressedVariants && image.smallFilename) {
        const filename = image.largeFilename || image.filename;
        return `${import.meta.env.VITE_RESOURCES_ENDPOINT}/${layer.root}/${filename}`;
      }
      return null; // No upgrade needed
    });
  }, [position.entries, layer]);

  const textures = useLoader(CredentialsTextureLoader, initialUrls);

  const groupRef = useRef<THREE.Group | null>(null);
  const [upgradedTextures, setUpgradedTextures] = useState<(THREE.Texture | null)[]>([]);

  // Reset upgraded textures when position changes to avoid showing stale images
  useEffect(() => {
    setUpgradedTextures([]);
  }, [position.entries[0].id]);

  // Load large images in background after initial render
  useEffect(() => {
    const needsUpgrade = largeUrls.some((url) => url !== null);
    if (!needsUpgrade) return;

    const loadLargeTextures = async () => {
      const loader = new CredentialsTextureLoader();
      const loaded: (THREE.Texture | null)[] = [];

      for (let i = 0; i < largeUrls.length; i++) {
        const url = largeUrls[i];
        if (!url) {
          loaded.push(null);
          continue;
        }

        try {
          const texture = await new Promise<THREE.Texture>((resolve, reject) => {
            loader.load(url, resolve, undefined, reject);
          });
          loaded.push(texture);
        } catch {
          loaded.push(null);
        }
      }

      setUpgradedTextures(loaded);
    };

    loadLargeTextures();
  }, [largeUrls, gl, position.entries]);

  // Use upgraded texture if available, otherwise use initial texture
  const activeTextures = useMemo(() => {
    return textures.map((tex, i) => upgradedTextures[i] || tex);
  }, [textures, upgradedTextures]);

  useEffect(() => {
    if (!position.entries[0].focalLengthX) {
      const maxAnisotropy = gl.capabilities.getMaxAnisotropy();
      activeTextures.forEach((texture) => applyEquirectangularTextureSettings(texture, maxAnisotropy));
    }
  }, [activeTextures, gl, position.entries]);

  const sphereGeom = useMemo(() => {
    const sphereGeom = new THREE.SphereGeometry(10, 64, 32);
    sphereGeom.rotateX(Math.PI / 2); // Align sphere UV poles with Z-up
    return sphereGeom;
  }, [textures]);

  const currentSettings = layerSettings[position.entries[0]?.layerId];
  const hasFilters =
    currentSettings &&
    ((currentSettings.imageBrightness ?? 0) !== 0 ||
      (currentSettings.imageContrast ?? 0) !== 0 ||
      (currentSettings.imageSaturation ?? 0) !== 0);

  return (
    <group ref={groupRef}>
      {position.entries.map((entry, i: number) => {
        const k = 100;

        // Common orientation
        const q = new THREE.Quaternion(
          entry.rotationQuaternion[0],
          entry.rotationQuaternion[1],
          entry.rotationQuaternion[2],
          entry.rotationQuaternion[3],
        );

        if (!entry.focalLengthX) {
          return (
            <EquirectEntry
              key={entry.id}
              entry={entry}
              texture={activeTextures[i]}
              sphereGeom={sphereGeom}
              settings={currentSettings}
            />
          );
        }

        // ----- Pinhole (6 images) -----
        // intrinsics (guard against nulls)
        const Wpx = entry.width;
        const Hpx = entry.height;
        const sx = entry.pixelWidth; // meters per pixel
        const sy = entry.pixelHeight; // meters per pixel
        const fx = entry.focalLengthX!; // meters
        const fy = entry.focalLengthY! || entry.focalLengthX!; // meters
        const cx = entry.principalPointX ?? Wpx / 2;
        const cy = entry.principalPointY ?? Hpx / 2;

        // derived sensor size in world units
        const Sx = Wpx * sx * k;
        const Sy = Hpx * sy * k;

        let d, planeWidth, planeHeight, tx, ty, dir, offset;

        let side;
        if (sx && sy) {
          // choose plane distance
          d = ((fx + fy) / 2) * k;

          // plane size at distance d
          planeWidth = (d / (fx * k)) * Sx; // = Sx if d==f*k
          planeHeight = (d / (fy * k)) * Sy;

          // local translation so plane center aligns with principal point
          tx = (cx - Wpx / 2) * sx * k;
          ty = (cy - Hpx / 2) * sy * k;

          // Plane center offset along the camera's local -Z (forward in E57; z<0)
          dir = new THREE.Vector3(0, 0, -1).applyQuaternion(q).normalize();
          side = THREE.FrontSide;
        } else {
          planeWidth = Wpx;
          planeHeight = Hpx;

          tx = cx - Wpx / 2;
          ty = cy - Hpx / 2;

          // Plane center offset along the camera's local Z (forward in Topodot; z>0)
          dir = new THREE.Vector3(0, 0, 1).applyQuaternion(q).normalize();
          side = THREE.BackSide;

          d = (entry.width + entry.height) / 4;

          activeTextures[i].flipY = false;
          // activeTextures[i].needsUpdate = true;
        }
        offset = dir.clone().multiplyScalar(d);

        const translation = new THREE.Vector3(tx, ty, 0).applyQuaternion(q);

        const pinholeMaterial =
          hasFilters && currentSettings
            ? createImageFilterMaterial(activeTextures[i], side, currentSettings)
            : undefined;

        return (
          <mesh key={entry.id} position={offset.toArray()} quaternion={q}>
            <group position={translation}>
              <mesh renderOrder={-9999} material={pinholeMaterial || undefined}>
                <planeGeometry args={[planeWidth, planeHeight]} />
                {!pinholeMaterial && (
                  <meshBasicMaterial
                    map={activeTextures[i]}
                    side={side}
                    depthWrite={false}
                    depthTest={false}
                    toneMapped={false}
                    fog={false}
                  />
                )}
              </mesh>
            </group>
          </mesh>
        );
      })}
    </group>
  );
};
